import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToasts } from '../../../cdl/Toast/useToasts';
import { deleteOrder } from '../../../common/api/clients/order.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';

export const useOrderDelete = () => {
    const queryClient = useQueryClient();
    const { addToast, addErrorToast } = useToasts();

    const mutation = useMutation({
        mutationFn: deleteOrder,
        onSuccess: (_, orderId) => {
            void queryClient.invalidateQueries({ queryKey: queries.orders._def });
            queryClient.removeQueries({ queryKey: queries.orders.detail(orderId).queryKey });
        },
    });

    const mutationOptions = {
        onSuccess: () => addToast(translate('order.deleteDraftToast.success')),
        onError: () => addErrorToast(translate('order.deleteDraftToast.error')),
    };

    type MutationOptions = NonNullable<Parameters<typeof mutation.mutate>[1]>;

    const getMutationOptions = (options?: MutationOptions): MutationOptions => ({
        ...options,
        onSuccess: (...args) => {
            mutationOptions.onSuccess();
            options?.onSuccess?.(...args);
        },
        onError: (...args) => {
            mutationOptions.onError();
            options?.onError?.(...args);
        },
    });

    const mutate: typeof mutation.mutate = (orderId, options) => mutation.mutate(orderId, getMutationOptions(options));
    const mutateAsync: typeof mutation.mutateAsync = (orderId, options) =>
        mutation.mutateAsync(orderId, getMutationOptions(options));

    return { ...mutation, mutate, mutateAsync };
};
